import clsx from 'clsx';
import type { RxDocument } from 'rxdb';
import { useEffect, useMemo, useState, useRef } from 'react';

import { Icon } from '@flintmint/web-shared/components/icon';
import { Input } from '@flintmint/web-shared/components/input';
import { Loader } from '@flintmint/web-shared/components/loader';
import { Tooltip } from '@flintmint/web-shared/components/tooltip';
import { Divider } from '@flintmint/web-shared/components/divider';
import { InlineConfirmation } from '@flintmint/web-shared/components/inline-confirmation';

import { useRouteInfo } from '@/hooks/use-route-info';
import { isAiCreditsExhausted, useResyncUserSource } from '@/hooks/use-resync-user-source';
import { useNavigateWithSearch } from '@/hooks/use-navigate-with-search';
import { useUserSourceCreate } from '@/hooks/rxdb-models';

import { isValidUrl, parseSource } from '@/utils/helper-utils';
import { openSourcePreviewInNewTab } from '@/utils/source-preview';

import type { UserSource } from '@flintmint/core-shared/rxdb';

import { appRoutes } from '@/constants/routes';

import styles from './source-input.module.scss';

export const SourceInput = (props: SourceInputProps) => {
  const { editable = true, showReSync = true, onCreateNew, userSource } = props;

  const { mutate: createUserSource } = useUserSourceCreate();

  const parsedSource = useMemo(() => parseSource(userSource), [userSource]);

  const [editing, setEditing] = useState(false);
  const [sourceValue, setSourceValue] = useState(parsedSource);
  const [openingSource, setOpeningSource] = useState(false);

  const inputRef = useRef<HTMLInputElement | null>(null);

  const { isCortex } = useRouteInfo();
  const navigateWithSearch = useNavigateWithSearch();

  const { resync, resyncWithUrl, isResyncing } = useResyncUserSource(userSource);

  const isUrlSource = userSource?.sourceType === 'url';
  const hasActions = !!userSource?.source && !editing;
  const hasError = isUrlSource && !isValidUrl(sourceValue) && editing;

  useEffect(() => {
    setSourceValue(parsedSource);
    setEditing(false);
  }, [editable, parsedSource]);

  useEffect(() => {
    const input = inputRef.current;
    // Only adjust caret/selection when this input is focused
    if (!editing && input && document.activeElement === input) {
      // Clear any text selection to prevent it from being restored
      window.getSelection()?.removeAllRanges();
      input.setSelectionRange(sourceValue.length, sourceValue.length);
    }
  }, [editing]);

  const openSource = () => {
    if (isUrlSource) {
      void openSourcePreviewInNewTab({ source: userSource!.source }).catch((error) => {
        console.error('Error retrieving file URL:', error);
      });
    } else {
      void previewUploadedFile();
    }
  };

  const previewUploadedFile = async () => {
    try {
      setOpeningSource(true);
      await openSourcePreviewInNewTab({ source: userSource!.source });
    } catch (error) {
      console.error('Error retrieving file URL:', error);
    } finally {
      setOpeningSource(false);
    }
  };

  const handleResyncWithUrl = () => {
    setEditing(false);
    if (isResyncing) {
      setSourceValue(parsedSource);
      return;
    }

    void resyncWithUrl(sourceValue).catch((error: unknown) => {
      // Refused for AI Energy: keep the typed URL open for editing so it can be confirmed again once Energy is back.
      if (isAiCreditsExhausted(error)) {
        setEditing(true);
        return;
      }
      // Revert the optimistic URL display if the resync request fails.
      setSourceValue(parsedSource);
    });
  };

  return (
    <InlineConfirmation
      appearance="dark"
      open={editing}
      error={hasError}
      onClose={() => {
        setEditing(false);
        setSourceValue(parsedSource);
      }}
      alert={{
        type: 'info',
        icon: 'information-diamond-bulk',
        title: 'Re-Sync or Create New',
        message: 'Do you want to re-sync the content from the URL or create a new Cortex Item',
        popoverProps: {
          placement: 'right-start',
          offset: [0, 15, -35] as [number, number, number],
        },
        primaryButton: {
          children: 'Create New',
          color: 'success' as const,
          onClick: () =>
            createUserSource(
              {
                name: sourceValue,
                sourceType: 'url',
                source: sourceValue,
                syncStatus: 'processing',
              },
              {
                onSuccess: (createdSource: UserSource) => {
                  setEditing(false);
                  onCreateNew?.();
                  if (isCortex) {
                    navigateWithSearch(`${appRoutes.cortex}/${createdSource.id}`);
                  }
                },
              }
            ),
        },
        secondaryButton: {
          children: 'Re-Sync',
          onClick: handleResyncWithUrl,
        },
      }}
    >
      <Input
        inline
        size="big"
        placeholder=""
        error={hasError}
        clearable={false}
        title={sourceValue}
        value={sourceValue}
        readOnly={!editing}
        inputRef={inputRef}
        corderRadius="smooth"
        appearance="filled-darker"
        data-testid="source-input"
        className={styles.sourceInput}
        message="The given URL is not correct"
        left={<span className={styles.left}>Source</span>}
        onChange={(e) => setSourceValue(e.target.value)}
        right={
          <>
            {hasActions && (
              <div
                className={clsx(styles.right, {
                  [styles.processing]: isResyncing,
                })}
              >
                {showReSync && (
                  <>
                    <Divider size={24} />
                    <Tooltip content="RySync">
                      <Icon type="reload" onClick={isResyncing ? undefined : resync} data-testid="resync" />
                    </Tooltip>
                  </>
                )}
                {editable && isUrlSource && (
                  <>
                    <Divider size={24} />
                    <Tooltip content="Edit">
                      <Icon type="edit-02" onClick={isResyncing ? undefined : () => setEditing(true)} data-testid="edit" />
                    </Tooltip>
                  </>
                )}
                <Divider size={24} />
                <Tooltip content="Open the Source">
                  {openingSource ? (
                    <Loader size={18} variant="steps" />
                  ) : (
                    <Icon type="link-square-02" onClick={openSource} data-testid="preview" />
                  )}
                </Tooltip>
              </div>
            )}
          </>
        }
      />
    </InlineConfirmation>
  );
};

export interface SourceInputProps {
  editable?: boolean;
  showReSync?: boolean;
  onCreateNew?: () => void;
  userSource?: RxDocument<UserSource>;
}
